import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Upload,
  BarChart3,
  Download,
  Server,
  Boxes,
  Code2,
  User,
  RefreshCw,
  CheckCircle2,
  Shield,
  Zap,
  KeyRound,
  ListChecks,
  Radar,
  Clock,
  X,
  ChevronLeft
} from 'lucide-react';
import Portal from './Portal';

// --- Small reusable primitives for a consistent, minimal look ---
const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
      {Icon && <Icon className="w-4 h-4 text-blue-600" />}
      {title}
    </h3>
    <div className="text-sm text-gray-600 space-y-2">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
    <span>{children}</span>
  </li>
);

const Step = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">
      {n}
    </span>
    <span className="text-gray-700">{children}</span>
  </li>
);

const WelcomeGuide = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const steps = [
    {
      title: 'Welcome to Smart Email Verifier',
      subtitle: 'A complete platform for verifying and cleaning your email lists',
      content: (
        <div className="space-y-5">
          <p className="text-gray-600">
            Smart Email Verifier helps you confirm that email addresses are real, remove invalid
            contacts, and protect your sender reputation. This short guide walks you through the
            key parts of the platform.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={Zap} title="Verify emails">
              <ul className="space-y-2">
                <Bullet>Single email validation</Bullet>
                <Bullet>Bulk list validation</Bullet>
                <Bullet>Real-time progress tracking</Bullet>
              </ul>
            </Card>
            <Card icon={Shield} title="Protect your deliverability">
              <ul className="space-y-2">
                <Bullet>Remove invalid and risky addresses</Bullet>
                <Bullet>Detect catch-all and spam traps</Bullet>
                <Bullet>Reduce bounce rates</Bullet>
              </ul>
            </Card>
            <Card icon={Server} title="Scale with satellites">
              <ul className="space-y-2">
                <Bullet>Distributed verification nodes</Bullet>
                <Bullet>Provider-specific modules</Bullet>
                <Bullet>Adaptive rate limiting</Bullet>
              </ul>
            </Card>
            <Card icon={Code2} title="Integrate via API">
              <ul className="space-y-2">
                <Bullet>Developer API access</Bullet>
                <Bullet>API keys and usage limits</Bullet>
                <Bullet>Export results programmatically</Bullet>
              </ul>
            </Card>
          </div>
        </div>
      ),
      action: 'Next: Verify Emails',
      actionType: 'next'
    },
    {
      title: 'Verify Emails',
      subtitle: 'Single and bulk email verification',
      content: (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={Mail} title="Single verification">
              <ol className="space-y-2">
                <Step n={1}>Open <strong>Single Validation</strong> from the sidebar.</Step>
                <Step n={2}>Enter the email address you want to check.</Step>
                <Step n={3}>Click <strong>Verify</strong> and review the result.</Step>
              </ol>
            </Card>
            <Card icon={Upload} title="Bulk list verification">
              <ol className="space-y-2">
                <Step n={1}>Open <strong>List Validation</strong> from the sidebar.</Step>
                <Step n={2}>Upload a CSV or TXT file with an <strong>email</strong> column.</Step>
                <Step n={3}>Track progress and download results when complete.</Step>
              </ol>
            </Card>
          </div>

          <Card icon={ListChecks} title="CSV file requirements">
            <ul className="space-y-2">
              <Bullet>First column must be named <strong>email</strong> (lowercase).</Bullet>
              <Bullet>Additional columns can be named anything and are preserved.</Bullet>
              <Bullet>Supported formats: CSV and TXT.</Bullet>
              <Bullet>Maximum file size: 10MB.</Bullet>
            </ul>
          </Card>

          <Card icon={Radar} title="Result statuses">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><span className="font-medium text-gray-900">Valid</span> — the address exists and can receive mail.</div>
              <div><span className="font-medium text-gray-900">Invalid</span> — the address does not exist.</div>
              <div><span className="font-medium text-gray-900">Catch All</span> — the domain accepts all mail; use with caution.</div>
              <div><span className="font-medium text-gray-900">Unknown</span> — status could not be determined.</div>
            </div>
          </Card>
        </div>
      ),
      action: 'Next: Results & Dashboard',
      actionType: 'next'
    },
    {
      title: 'Results & Dashboard',
      subtitle: 'Monitor progress and analyze outcomes',
      content: (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={BarChart3} title="Dashboard">
              <ul className="space-y-2">
                <Bullet>Statistics cards for total, valid, and invalid emails.</Bullet>
                <Bullet>Status distribution charts.</Bullet>
                <Bullet>Recent jobs table with real-time progress.</Bullet>
              </ul>
            </Card>
            <Card icon={Download} title="Export results">
              <ul className="space-y-2">
                <Bullet>Download results in CSV format.</Bullet>
                <Bullet>Filter by status before exporting.</Bullet>
                <Bullet>Original data columns are preserved.</Bullet>
                <Bullet>Verification methods are included per email.</Bullet>
              </ul>
            </Card>
          </div>

          <Card icon={Clock} title="Background processing">
            <p>
              For large lists, verification runs in the background. You can close the browser and
              come back later — progress is saved automatically.
            </p>
          </Card>
        </div>
      ),
      action: 'Next: Leads & Downloads',
      actionType: 'next'
    },
    {
      title: 'Leads & Downloads',
      subtitle: 'Manage your contacts and download lists',
      content: (
        <div className="space-y-5">
          <Card icon={User} title="Lead management">
            <ul className="space-y-2">
              <Bullet>View and manage all verified leads in one place.</Bullet>
              <Bullet>Filter and search by status, domain, or email.</Bullet>
              <Bullet>Perform batch actions on selected leads.</Bullet>
            </ul>
          </Card>

          <Card icon={Download} title="Download lists">
            <ul className="space-y-2">
              <Bullet>Access completed verification lists from <strong>Download Lists</strong>.</Bullet>
              <Bullet>Re-download previous exports at any time.</Bullet>
              <Bullet>Filter results by status before downloading.</Bullet>
            </ul>
          </Card>
        </div>
      ),
      action: 'Next: Satellites & Modules',
      actionType: 'next'
    },
    {
      title: 'Satellites & Modules',
      subtitle: 'Distributed verification and provider management',
      content: (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={Server} title="Satellite nodes">
              <ul className="space-y-2">
                <Bullet>Distribute verification across multiple VPS nodes.</Bullet>
                <Bullet>Monitor node health and status.</Bullet>
                <Bullet>Each node uses its own IP and burner domain.</Bullet>
              </ul>
            </Card>
            <Card icon={Boxes} title="Modules">
              <ul className="space-y-2">
                <Bullet>Provider-specific verification logic.</Bullet>
                <Bullet>Enable or disable individual providers.</Bullet>
                <Bullet>Manage provider credentials and limits.</Bullet>
              </ul>
            </Card>
          </div>

          <Card icon={Shield} title="Why this matters">
            <p>
              Spreading verification across many IPs keeps any single node from being flagged,
              improves throughput, and lets provider-specific logic run without interfering with
              other checks.
            </p>
          </Card>
        </div>
      ),
      action: 'Next: API Access',
      actionType: 'next'
    },
    {
      title: 'API Access',
      subtitle: 'Integrate verification into your own applications',
      content: (
        <div className="space-y-5">
          <Card icon={KeyRound} title="API keys">
            <ul className="space-y-2">
              <Bullet>Create and manage API keys from <strong>API Access</strong>.</Bullet>
              <Bullet>Set daily email and list limits per key.</Bullet>
              <Bullet>Revoke keys at any time to stop access.</Bullet>
            </ul>
          </Card>

          <Card icon={Code2} title="Developer integration">
            <ul className="space-y-2">
              <Bullet>Authenticate with your API key.</Bullet>
              <Bullet>Submit single or bulk verification requests.</Bullet>
              <Bullet>Retrieve results as JSON for your own workflows.</Bullet>
            </ul>
          </Card>
        </div>
      ),
      action: 'Next: Settings & Advanced',
      actionType: 'next'
    },
    {
      title: 'Settings & Advanced',
      subtitle: 'SMTP/IMAP, rate limiting, and verification methods',
      content: (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={Mail} title="SMTP & IMAP credentials">
              <ul className="space-y-2">
                <Bullet>Configure SMTP sending credentials.</Bullet>
                <Bullet>Enable IMAP verification with inbox checks.</Bullet>
                <Bullet>SMTP and IMAP are configured together to avoid confusion.</Bullet>
              </ul>
            </Card>
            <Card icon={Zap} title="Concurrency & rate limiting">
              <ul className="space-y-2">
                <Bullet>Adjust SMTP and worker concurrency.</Bullet>
                <Bullet>Provider-specific cooldowns (Yahoo, Microsoft, etc.).</Bullet>
                <Bullet>Retry attempts with progressive delays.</Bullet>
              </ul>
            </Card>
          </div>

          <Card icon={Radar} title="Verification methods">
            <ul className="space-y-2">
              <Bullet><strong>SMTP probe</strong> — direct connection to the mail server.</Bullet>
              <Bullet><strong>MX record check</strong> — DNS validation of the domain.</Bullet>
              <Bullet><strong>Catch-all detection</strong> — analyze domain behavior.</Bullet>
              <Bullet><strong>IMAP verification</strong> — confirm delivery and detect bounces.</Bullet>
              <Bullet><strong>Spam trap detection</strong> — flag risky addresses.</Bullet>
            </ul>
          </Card>

          <Card icon={Shield} title="Getting the best results">
            <ul className="space-y-2">
              <Bullet>Start conservative with lower concurrency and increase gradually.</Bullet>
              <Bullet>Test settings with a small list before bulk verification.</Bullet>
              <Bullet>Respect provider rate limits to avoid IP blacklisting.</Bullet>
            </ul>
          </Card>
        </div>
      ),
      action: 'Next: Updates & Profile',
      actionType: 'next'
    },
    {
      title: 'Updates & Profile',
      subtitle: 'Keep the platform current and manage your account',
      content: (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={RefreshCw} title="App updates">
              <ul className="space-y-2">
                <Bullet>Check for and install the latest releases.</Bullet>
                <Bullet>View the current version and changelog.</Bullet>
                <Bullet>Keep the platform up to date for fixes and features.</Bullet>
              </ul>
            </Card>
            <Card icon={User} title="Profile & account">
              <ul className="space-y-2">
                <Bullet>Update your account details and password.</Bullet>
                <Bullet>Review your plan and usage.</Bullet>
                <Bullet>Manage session and security preferences.</Bullet>
              </ul>
            </Card>
          </div>

          <Card icon={CheckCircle2} title="You're ready">
            <p>
              You now have a full picture of the platform. Start by verifying a single email or
              uploading your first list, and explore the other pages as you go.
            </p>
          </Card>
        </div>
      ),
      action: 'Get Started',
      actionType: 'finish'
    }
  ];

  const handleAction = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
      navigate('/dashboard');
    }
  };

  const handleSkip = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-5 relative z-10">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">{steps[currentStep].title}</h1>
                  <p className="text-sm text-gray-500 mt-0.5">{steps[currentStep].subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {currentStep + 1} of {steps.length}
                </span>
                <button
                  onClick={handleSkip}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close guide"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-1 bg-gray-100 rounded-full">
              <div
                className="h-1 bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {steps[currentStep].content}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-200 relative z-10">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Skip Guide
            </button>

            <div className="flex items-center gap-3">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors inline-flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
              )}
              <button
                onClick={handleAction}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
              >
                {steps[currentStep].action}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default WelcomeGuide;
